import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface EventData {
  title: string;
  subtitle: string;
  seller: { id: string; displayName: string; profileImageUrl: string | null };
  items: { id: string; name: string; imageUrl: string | null }[];
}

export default function EventClaim() {
  const params = useParams();
  const slug = params.slug || "";
  const { toast } = useToast();

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  // Silently detected from the browser (they're scanning on their phone), so the
  // later app-download email links to TestFlight vs Firebase. No prompt needed.
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const phoneType = /iPhone|iPad|iPod/i.test(ua) ? "ios" : /Android/i.test(ua) ? "android" : "";
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<null | "pending" | "verified" | "existing">(null);

  const { data, isLoading, isError } = useQuery<EventData>({
    queryKey: ["/api/event", slug],
    queryFn: async () => {
      const res = await fetch(`/api/event/${encodeURIComponent(slug)}`);
      if (!res.ok) throw new Error("not found");
      return res.json();
    },
  });

  const submit = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast({ title: "Enter a valid email to continue", variant: "destructive" });
      return;
    }
    if (!selectedItem) {
      toast({ title: "Pick a treat first", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/event/${encodeURIComponent(slug)}/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itemId: selectedItem,
          email: email.trim(),
          firstName: firstName.trim(),
          phoneType,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.message || "Something went wrong");
      }
      setResult(
        body?.status === "verified" ? "verified" : body?.status === "existing" ? "existing" : "pending",
      );
    } catch (e: any) {
      toast({ title: "Couldn't claim", description: e?.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
        <p className="text-muted-foreground">This event link isn't active.</p>
      </div>
    );
  }

  const selectedName = data.items.find((i) => i.id === selectedItem)?.name;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-md px-4 py-8">
        {result ? (
          <Card className="rounded-2xl">
            <CardContent className="space-y-3 p-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-6 w-6 text-primary" />
              </div>
              {result === "existing" ? (
                <>
                  <h1 className="text-2xl font-semibold">You're already on Stoop! 🎉</h1>
                  <p className="text-sm text-muted-foreground">
                    The free sample is our way of welcoming new folks, but thanks so much for stopping by. Go grab
                    something delicious from {data.seller.displayName} on Stoop!
                  </p>
                </>
              ) : result === "verified" ? (
                <>
                  <h1 className="text-2xl font-semibold">You're all set{firstName ? `, ${firstName}` : ""}! 🍪</h1>
                  <p className="text-sm text-muted-foreground">
                    Your email is confirmed. Show this at the {data.seller.displayName} table to grab your{" "}
                    {selectedName ? <span className="font-medium text-foreground">{selectedName}</span> : "treat"}.
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-semibold">Almost there! 📩</h1>
                  <p className="text-sm text-muted-foreground">
                    We sent a confirmation link to <span className="font-medium text-foreground">{email}</span>. Open
                    it and tap <span className="font-medium text-foreground">Confirm my email</span>, then show that
                    screen at the {data.seller.displayName} table to grab your treat.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Don't see it? Check spam, or ask us at the table.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <h1 className="mb-1 text-2xl font-semibold">{data.title}</h1>
            <p className="mb-6 text-sm text-muted-foreground">{data.subtitle}</p>

            <Card className="mb-5 rounded-2xl">
              <CardContent className="space-y-2 p-4">
                <div className="mb-1 text-sm font-medium">Pick one</div>
                {data.items.map((item) => {
                  const active = selectedItem === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedItem(item.id)}
                      className={`flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition ${
                        active ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                          active ? "border-primary bg-primary text-white" : "border-muted-foreground/40"
                        }`}
                      >
                        {active && <Check className="h-3.5 w-3.5" />}
                      </span>
                      {item.imageUrl && (
                        <img src={item.imageUrl} alt="" className="h-10 w-10 rounded-lg object-cover" />
                      )}
                      <span className="min-w-0 flex-1 text-sm font-medium">{item.name}</span>
                      <span className="text-sm font-semibold text-primary">Free</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm leading-6 text-foreground">
                Enter your email to claim your free treat. We'll also send a link to download the Stoop app — and the{" "}
                <span className="font-medium">first 10 people to download and create an account get free delivery from{" "}
                {data.seller.displayName}</span> on future orders.
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="event-email">
                  Email
                </label>
                <Input
                  id="event-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="event-name">
                  First name <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <Input
                  id="event-name"
                  type="text"
                  autoComplete="given-name"
                  placeholder="Alex"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>

              <Button
                size="lg"
                className="w-full rounded-full"
                onClick={submit}
                disabled={submitting || !selectedItem || !email.trim()}
              >
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Claim your treat
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
